import {
  ApplicationScope, Customer,
  CustomerShort, Database, DatabasesQuota, InvoiceStatus,
  MyDbCustomerSubscription,
  Subscription,
  SubscriptionStatus, TenantCustomerSubscription
} from "~/api-client";
import { defineStore } from "pinia";

export interface CustomerWebspaceUsage extends MyDbCustomerSubscription {
  id: number,
  username: string,
  firstName: string,
  lastName: string,
  customerDetails: CustomerShort,
  databases?: Database[],
  hasChildren?: boolean
}

interface State {
  _currentUserSubscriptions: Subscription[],
  _customerWebspaceUsages: CustomerWebspaceUsage[]
  _totalStorageCapacityInMb: number,
  _totalStorageCapacityUsedInMb: number,
  _totalDatabases: number,
  _totalDatabasesUsed: number
}


export const useSubscriptionStore = defineStore('subscription', {
  state: (): State => ({
    _currentUserSubscriptions: [],
    _customerWebspaceUsages: [],
    _totalStorageCapacityInMb: 0,
    _totalStorageCapacityUsedInMb: 0,
    _totalDatabases: 0,
    _totalDatabasesUsed: 0
  }),

  getters: {
    currentUserSubscriptions: (state) => state._currentUserSubscriptions,
    currentUserSubscriptionByOrderId: (state) => (orderId: string) => state._currentUserSubscriptions.find((sub: Subscription )=> sub.orderId === orderId),
    isCurrentUserSubscriptionActive: (state) => state._currentUserSubscriptions.some(sub => sub.status === SubscriptionStatus.Active),
    isCurrentUserSubscriptionOpenForPayment: (state) => state._currentUserSubscriptions.find((sub) => new Date().valueOf() < sub.validTo!.valueOf() && sub.status === SubscriptionStatus.OutstandingBalance),
    currentUserActiveSubscription: (state) =>
      state._currentUserSubscriptions.find((sub: Subscription) => (sub.status === SubscriptionStatus.Active || sub.status === SubscriptionStatus.ActiveButCancelledForUpcoming)),
    shopNotAvailableForCurrentUser: (state) => {
      // const subs: Subscription[] = state._currentUserSubscriptions.filter((sub: Subscription) => (sub.status === SubscriptionStatus.Active || sub.status === SubscriptionStatus.ActiveButCancelledForUpcoming || sub.status === SubscriptionStatus.Upcoming));
      const subs: Subscription[] = state._currentUserSubscriptions;
      const listStatuses: SubscriptionStatus[] | undefined = subs.map(sub => sub.status);
      return (listStatuses?.includes(SubscriptionStatus.Active) || (listStatuses?.includes(SubscriptionStatus.Upcoming) && listStatuses?.includes(SubscriptionStatus.ActiveButCancelledForUpcoming)) || subs.some(sub => new Date().valueOf() < sub.validTo!.valueOf() ));
      },
    isSubscriptionByIdActive: (state) => (subId: string) => {
      const subToBeChecked =  state._currentUserSubscriptions.find((sub: Subscription)=> (sub.id === subId));
      return subToBeChecked !== null ? (subToBeChecked!.status === SubscriptionStatus.Active) : false;
    },
    isSubscriptionByIdSuspended: (state) => (subId: string) => {
      const subToBeChecked = state._currentUserSubscriptions.find((sub: Subscription) => sub.id === subId);
      if (!subToBeChecked || subToBeChecked.status !== SubscriptionStatus.Suspended) return false;
      // Abgelaufene Abos können nicht reaktiviert werden
      if (subToBeChecked.validTo && new Date(subToBeChecked.validTo).getTime() < Date.now()) return false;
      return true;
    },
    allRunningCustomerWebspaceUsages: (state) => {return state._customerWebspaceUsages},
    totalUsages: (state) => {
      return {numDatabasesUsed: state._totalDatabasesUsed, numDatabasesAvailable: state._totalDatabases, storageCapacityUsedInMb: state._totalStorageCapacityUsedInMb, storageCapacityInMb: state._totalStorageCapacityInMb} as DatabasesQuota;
    }
  },
  actions: {
    async getCurrentUserSubscriptions() {
      const {$backendService , $logger, $tenant} = useNuxtApp();
      return $backendService.subscriptionControllerApi.getCurrentUserSubscriptions({xApplicationScope: $tenant.getTenant})
        .then(result => {
          this.$state._currentUserSubscriptions = result;
          return result;
        }).catch(error => {
          ElMessage.error("Beim der Aktualisierung der Tabelle ist ein Fehler aufgetreten");
          throw error;
        });
    },
    async toggleDatabaseAdminLock(customerId: string, databaseId: string){
      const {$backendService , $logger, $tenant} = useNuxtApp();
      return $backendService.databaseAdminControllerApi.toggleDatabaseAdminLock({xApplicationScope: ApplicationScope.MyDb,customerId: customerId, databaseId: databaseId})
        .then(result => {
          this._customerWebspaceUsages = this.allRunningCustomerWebspaceUsages.map(wu => {
            if (wu.customerId === customerId){
              wu.databases = wu.databases!.map(db => {
                if (db.id === databaseId){
                  db.adminLocked = result.adminLocked;
                }
                return db;
              })
            }
            return wu;
          })
          if (result.adminLocked) {
            ElMessage.success("Die Datenbank wurde gesperrt")
          } else {
            ElMessage.success("Die Datenbank wurde entsperrt")
          }
        }).catch(error => {
          ElMessage.error("Beim Sperren/Entsperren der Datenbank ist ein Fehler aufgetreten")
        })
    },
    async cancelSubscription(subId: string){
      const {$backendService , $logger, $tenant} = useNuxtApp();
      if (this.currentUserSubscriptions.some(sub => sub.status === SubscriptionStatus.Upcoming && sub.id !== subId)){
        ElMessage.error("Sie müssen zuerst die vorgemerkte Änderung Ihres Abos zurücknehmen.");
        return;
      }
      return $backendService.subscriptionControllerApi.cancelSubscription({xApplicationScope: $tenant.getTenant, id: subId })
        .then(async() => {
          await this.getCurrentUserSubscriptions();
        }).catch(error => {
          ElMessage.error("Bei der Aktualisierung der Tabelle ist ein Fehler aufgetreten");
          throw error;
        });

    },
    async reactivateSubscription(subId: string){
      const {$backendService , $logger, $tenant} = useNuxtApp();
      return $backendService.subscriptionControllerApi.reactivateSubscription({xApplicationScope: $tenant.getTenant, id: subId })
        .then(async() => {
          await this.getCurrentUserSubscriptions();
        }).catch(error => {
          ElMessage.error("Bei der Aktualisierung der Tabelle ist ein Fehler aufgetreten");
          throw error;
        });
    },
    async getAllRunningTenantCustomerSubscriptions(){
      const {$backendService , $logger, $tenant, $customerStore, $mydb} = useNuxtApp();
      await $customerStore.getCustomers();
      return $backendService.subscriptionControllerApi.getAllRunningTenantCustomerSubscriptions({
        xApplicationScope: $tenant.getTenant
      }).then(result => {
        this._totalDatabases = this._totalDatabasesUsed = this._totalStorageCapacityInMb = this._totalStorageCapacityUsedInMb = 0;
        let currentId: number = 0;
        this.$state._customerWebspaceUsages = result.map((res: TenantCustomerSubscription) => {
          currentId = ++currentId;
          const customer: CustomerShort = $customerStore.customerById(res.customerId!)
          this._totalStorageCapacityUsedInMb = this._totalStorageCapacityUsedInMb + (<MyDbCustomerSubscription>res).storageCapacityUsedInMb!;
          this._totalStorageCapacityInMb = this._totalStorageCapacityInMb + (<MyDbCustomerSubscription>res).storageCapacityInMb!;
          this._totalDatabasesUsed = this._totalDatabasesUsed + (<MyDbCustomerSubscription>res).numDatabasesUsed!;
          this._totalDatabases = this._totalDatabases + (<MyDbCustomerSubscription>res).numDatabasesAvailable!;
          const hasChildren = (!!(<MyDbCustomerSubscription>res).databases && (<MyDbCustomerSubscription>res).databases!.length !== 0) ? { hasChildren: true} : {hasChildren: false};
          return {...res, firstName: customer?.firstName, lastName: customer?.lastName, username: customer?.email,
            customerDetails: customer, ...hasChildren, id: currentId} as CustomerWebspaceUsage;
        });
      }).catch(error => {
        ElMessage.error("Bei der Aktualisierung der Tabelle ist ein Fehler aufgetreten");
        throw error;
      })
    },
    async updateInvoiceStatusOnCurrentSubscriptionState(invoiceId: string, invoiceStatus: InvoiceStatus) {
      this.$state._currentUserSubscriptions = this.$state._currentUserSubscriptions.map((sub: Subscription )=> {
        if (!!sub.invoices && sub.invoices.length > 0) {
          sub.invoices = sub.invoices.map(invoice => {
            if (invoice.id === invoiceId){
              invoice.status = invoiceStatus;
            }
            return invoice;
          })
        }
        return sub;
      });
    },
    async updateInvoiceRequestStatusOnCurrentSubscriptionState(invoiceId: string, requestStatus: boolean) {
      this.$state._currentUserSubscriptions = this.$state._currentUserSubscriptions.map((sub: Subscription )=> {
        if (!!sub.invoices && sub.invoices.length > 0) {
          sub.invoices = sub.invoices.map(invoice => {
            if (invoice.id === invoiceId){
              invoice.requestStatusChange = requestStatus;
            }
            return invoice;
          })
        }
        return sub;
      });
    },
    async handleAllRunningTenantCustomerSubscriptionsAfterDbDeletion(customerId: string, dbId: string){
      const {$backendService , $logger, $tenant, $customerStore, $mydb} = useNuxtApp();

      $backendService.databaseAdminControllerApi.getQuotaAsSuperadmin({xApplicationScope: $tenant.getTenant, customerId: customerId})
        .then(res => {
          let numDatabaseToBeRemoved = 0;
          let storageCapacityToBeRemoved: 0.0;
          this.$state._customerWebspaceUsages = this.allRunningCustomerWebspaceUsages.map(wu => {
            if (wu.customerId === customerId){
              wu.databases = wu.databases!.filter(db => db.id !== dbId)
              numDatabaseToBeRemoved = wu.numDatabasesUsed! - res.numDatabasesUsed!
              wu.numDatabasesUsed = res.numDatabasesUsed;
              storageCapacityToBeRemoved = wu.storageCapacityUsedInMb! - res.storageCapacityUsedInMb!
              wu.storageCapacityUsedInMb = res.storageCapacityUsedInMb;
            }
            return wu;
          })
          this._totalDatabasesUsed = this._totalDatabasesUsed - numDatabaseToBeRemoved;
          this._totalStorageCapacityUsedInMb = this._totalStorageCapacityUsedInMb - storageCapacityToBeRemoved;
        }).catch(error => {
          this.$state._customerWebspaceUsages = this.allRunningCustomerWebspaceUsages.map(wu => {
            if (wu.customerId === customerId){
              wu.databases = wu.databases!.filter(db => db.id !== dbId)
              wu.numDatabasesUsed = wu.numDatabasesUsed! - 1;
            }
            return wu;
          });
          ElMessage.error("Bei der Aktualisierung der Tabelle ist ein Fehler aufgetreten")
          throw error;
      })
    }
  }
})
